// @flow

import { createTheme, experimental_sx as sx } from '@mui/material/styles';
import { unstable_createCssVarsProvider } from '@mui/system';
import lightblue from '@mui/material/colors/lightBlue';
import blue from '@mui/material/colors/blue';
import teal from '@mui/material/colors/teal';
import orange from '@mui/material/colors/orange';
import blueGrey from '@mui/material/colors/blueGrey';
import deepPurple from '@mui/material/colors/deepPurple';
import grey from '@mui/material/colors/grey';

import AuthorLio from './assets/author-lio-slama.jpeg';

type Theme = Object

const NB_50 = 50;
const NB_100 = 100;
const NB_200 = 200;
const NB_300 = 300;
const NB_400 = 400;
const NB_500 = 500;
const NB_600 = 600;
const NB_700 = 700;
const NB_800 = 800;
const drawerWidth = 115; // theme.spacing(7)
const headerHeight = 70;

// List of CSS properties that should preserve numeric values as pixels
const PIXEL_PROPERTIES = [
  'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
  'width', 'height', 'maxWidth', 'maxHeight', 'minWidth', 'minHeight',
  'top', 'right', 'bottom', 'left',
  'borderRadius', 'borderWidth', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
  'fontSize', 'lineHeight', 'letterSpacing',
  'gap', 'rowGap', 'columnGap'
];

// Create a custom spacing function
const createCustomSpacing = () => {
  // This will be our spacing function
  const spacingFn = (factor) => {
    if (factor === undefined) {
      return '8px'; // Default spacing unit
    }
    
    // For arrays, process each value recursively
    if (Array.isArray(factor)) {
      return factor.map((val) => typeof val === 'number' ? `${val * 8}px` : val);
    }
    
    // When used as theme.spacing(n), multiply the value by 8 as the standard MUI behavior
    return `${factor * 8}px`;
  };
  
  // Add a custom property to identify this function as our special handler
  spacingFn.isCustomSpacing = true;
  
  return spacingFn;
};

const statusColors = {
  pending: {
    main: '#f44336'
  },
  current: {
    main: '#ffc107'
  },
  finished: {
    main: '#4b8a30'
  },
  info: {
    main: '#2196f3' // lightblue[NB_600]
  },
  error: {
    main: '#FF0000'
  }
};

// Create and customize theme
const baseTheme = createTheme({
  // Standard MUI spacing (8px unit)
  spacing: 8,
  palette: {
    background: {
      paper: '#fff',
      default: '#fafafa'
    },
    primary: {
      lighter: '#A6B3F5', // '#bbdefb',
      light: '#7E8EE9', // '#64b5f6',
      main: '#5c6bc0', // '#2196f3',
      dark: '#4A4DA7', // '#1b77c0',
      contrastText: '#fff'
    },
    secondary: {
      light: teal[NB_300],
      main: '#ffeb3a',
      dark: teal[NB_700]
    },
    text: {
      main: '#000000'
    },
    error: {
      main: '#f44336'
    },
    success: {
      main: statusColors.finished.main
    },
    info: {
      main: statusColors.info.main
    },
    warning: {
      main: statusColors.current.main
    },
    custom: {
      disabledText: 'hsl(0, 0%, 40%)',
      backgroundTextInfo: '#5b5b5b',
      neoYellow: {
        light: '#FFEF64',
        main: '#FFEB3B',
        dark: '#EDDA37'
      },
      neoViolet: {
        main: '#5c6bc0',
        dark: '#4A4DA7'
      }
    },
    messageDeliveryStatuses: {
      sending: '#B2B2B2',
      delivered: '#2196f3', // 009210',
      failed: '#ff5858'
    },
    status: statusColors,
    tags: {
      maintenance: {
        main: '#6B9592'
      },
      rent: {
        main: '#425C5A'
      },
      complaint: {
        main: '#FFAB60'
      },
      moving: {
        main: '#FFB472'
      },
      other: {
        main: '#A2BFBD'
      }
    },
    border: {
      color: 'hsl(0,0%,80%)'
    },
    table: {
      cellBackground: 'hsl(0,0%,100%)'
    }
  },
  props: {
    MuiButtonBase: {
      // The properties to apply
      disableRipple: true, // No more ripple, on the whole application 💣!
      border: '1px solid rgba(0, 0, 0, 0.23)'
    },
    statusIconsColors: {
      '&.info': {
        color: statusColors.info.main
      },
      '&.pending': {
        color: statusColors.pending.main
      },
      '&.current': {
        color: statusColors.current.main
      },
      '&.finished': {
        color: statusColors.finished.main
      }
    }
  },
  typography: {
    fontFamily: [
      'Product Sans'
    ]
  },
  layout: {
    responsiveScreenHeight: 'var(--vh, 1vh) * 100',
    listTable: {
      rowHeight: 55
    },
    customProps: {
      drawer: {
        width: drawerWidth,
        backgroundColor: blueGrey[NB_50]
      },
      header: {
        height: headerHeight
      },
      gray: '#838383'
    },
    content: {
      flexGrow: 1
    },
    debug: {
      public: {
        background: 'green'
      },
      member: {
        background: '#E0EBF5'
      }
    }
  }
});

baseTheme.layout.customProps.blockquote = (WIDTH) => ({
  position: 'relative',
  paddingTop: baseTheme.spacing(1),
  paddingBottom: baseTheme.spacing(1),
  width: WIDTH,
  fontSize: '1rem',
  '& blockquote': {
    marginInlineStart: baseTheme.spacing(4),
    marginInlineEnd: baseTheme.spacing(4)
  },
  // lineHeight: '2rem',
  // paddingLeft: theme.spacing(1),
  fontStyle: 'italic',
  backgroundColor: baseTheme.palette.custom.neoYellow.main,
  borderRadius: baseTheme.spacing(2),
  '& div.author': {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    textAlign: 'right',
    fontSize: '1rem',
    fontWeight: 700,
    lineHeight: '2.5rem',
    marginTop: '10px',
    fontStyle: 'normal',
    // textTransform: 'uppercase'
    '& .authorLio': {
      position: 'relative',
      display: 'inline-block',
      marginRight: baseTheme.spacing(2),
      right: 0,
      width: baseTheme.spacing(6),
      height: baseTheme.spacing(6),
      borderRadius: baseTheme.spacing(6),
      backgroundImage: `url(${AuthorLio})`,
      backgroundSize: 'cover'
    }
  },
  [baseTheme.breakpoints.down('xs')]: {
    maxWidth: `calc(100vw - ${baseTheme.spacing(2)}px)`
  }
});

// MUI 5 uses 'components' instead of 'overrides'
baseTheme.components = {
  MuiPopover: {
    styleOverrides: {
      root: {
        borderRadius: 0
      },
      paper: {
        borderRadius: 0
      }
    }
  },
  MuiTableCell: {
    styleOverrides: {
      body: {
        fontSize: '0.9rem'
      }
    }
  },
  MuiButton: {
    styleOverrides: {
      containedSecondary: {
        backgroundColor: baseTheme.palette.custom.neoYellow.main,
        color: 'black',
        '&:hover': {
          backgroundColor: baseTheme.palette.custom.neoYellow.dark
        }
      }
    }
  },
  MuiToggleButton: {
    styleOverrides: {
      root: {
        lineHeight: 1.5
      }
    }
  },
  MuiInputBase: {
    styleOverrides: {
      root: {
        fontSize: '0.9rem'
      }
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: '#428bc0'
        }
      },
      input: {
        fontSize: '1rem',
        '&::placeholder': {
          color: '#2a84c6'
        }
      }
    }
  },
  MuiChip: {
    styleOverrides: {
      root: {
        height: 20,
        color: 'white'
      }
    }
  },
  MuiFab: {
    styleOverrides: {
      secondary: {
        width: baseTheme.spacing(6),
        height: baseTheme.spacing(6),
        backgroundColor: '#FAFAFA',
        '&:hover': {
          backgroundColor: '#EAEAEA'
        }
      }
    }
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        '& *': {
          fontWeight: 600,
          fontSize: '1.4rem'
        }
      }
    }
  }
};


baseTheme.typography = {
  ...baseTheme.typography,
  root: {
    fontSize: 11
  },
  h1: {
    fontSize: '4rem',
    fontWeight: 500,
    lineHeight: 1.6
  },
  h2: {
    fontSize: '1.5rem',
    fontWeight: 600,
    lineHeight: 1.7
  },
  h3: {
    fontSize: '1.3rem',
    fontWeight: 600,
    lineHeight: 1.3
  },
  h4: {
    fontSize: '1.3rem',
    fontWeight: 400
  },
  h5: {
    fontSize: '1.2rem',
    fontWeight: 400
  },
  body1: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5
  },
  body2: {
    fontSize: '0.8125rem',
    fontWeight: 400,
    lineHeight: 1.5
  },
  content: {
    fontSize: '0.875rem',  // 14px - standard content text size (used by tags, inline content)
    fontWeight: 400,
    lineHeight: 1.5
  },
  fontFamily: [
    'Product Sans'
  ],
  pre: {
    fontFamily: ['Product Sans', 'sans-serif'].join(','),
    whiteSpace: 'pre-wrap'
  }
};

// Add Dialog-related component overrides to the components object
baseTheme.components.MuiDialog = {
  styleOverrides: {
    paper: {
      width: 600,
      margin: 0,
      [baseTheme.breakpoints.down('sm')]: {
        width: '90vw'
      }
    },
    paperWidthSm: {
      maxWidth: 600,
      [baseTheme.breakpoints.down('sm')]: {
        maxWidth: '90vw'
      }
    }
  }
};
baseTheme.components.MuiDialogActions = {
  styleOverrides: {
    root: {
      margin: `0px ${baseTheme.spacing(3)}px ${baseTheme.spacing(2)}px ${baseTheme.spacing(3)}px`,
      [baseTheme.breakpoints.down('sm')]: {
        margin: `0px ${baseTheme.spacing(3)}px ${baseTheme.spacing(1)}px ${baseTheme.spacing(3)}px`
      }
    }
  }
};
baseTheme.components.MuiDialogContent = {
  styleOverrides: {
    root: {
      padding: `0 ${baseTheme.spacing(3)}px`
    }
  }
};
baseTheme.components.MuiPaper = {
  styleOverrides: {
    rounded: {
      borderRadius: 8,
      [baseTheme.breakpoints.down('sm')]: {
        borderRadius: 8
      }
    }
  }
};
baseTheme.shape = {
  borderRadius: 8,
  [baseTheme.breakpoints.down('sm')]: {
    borderRadius: 8
  }
};
baseTheme.layout.customProps.toolbar = {
  height: headerHeight,
  minHeight: headerHeight * 2,
  [baseTheme.breakpoints.down('sm')]: {
    minHeight: headerHeight * 2 + baseTheme.spacing(1)
  }
};
baseTheme.layout.customProps.toolbarHalf = {
  height: headerHeight,
  minHeight: headerHeight,
  [baseTheme.breakpoints.down('sm')]: {
    minHeight: headerHeight + baseTheme.spacing(1)
  }
};
baseTheme.layout.content = {
  flexGrow: 1,
  [baseTheme.breakpoints.down('sm')]: {
    paddingTop: headerHeight * 2 + baseTheme.spacing(1) // Account for header + subheader on mobile
  }
};
baseTheme.layout.contentHalf = {
  flexGrow: 1,
  [baseTheme.breakpoints.down('sm')]: {
    paddingTop: headerHeight + baseTheme.spacing(1) // Account for header only (no subheader) on mobile
  }
};
baseTheme.squareplan = {
  window: {
    width: {
      width: 1000,
      maxWidth: 1000,
      [baseTheme.breakpoints.only('xs')]: {
        width: `calc(100vw - ${baseTheme.spacing(10)}px)`
      },
      [baseTheme.breakpoints.only('sm')]: {
        width: `calc(100vw - ${baseTheme.spacing(16)}px)`
      },
      [baseTheme.breakpoints.up('md')]: {
        width: `calc(100vw - ${baseTheme.spacing(36)}px)`
      }
    },
    height: {
      height: 600,
      maxheight: 600,
      [baseTheme.breakpoints.only('xs')]: {
        maxheight: '100%'
      },
      [baseTheme.breakpoints.only('sm')]: {
        maxheight: '100%'
      },
      [baseTheme.breakpoints.up('md')]: {
        maxheight: '100%'
      }
    }
  }
  
};

export default baseTheme;